import isNil from 'lodash/isNil'
import {
  ClientOptionDto,
  InvoiceTemplate,
  PreviewDraftEmailRequestDto,
} from '../../../api/dto/invoice-delivery.dto'
import { UploadInvoiceFileOptions } from '../../../api/invoice-delivery.api'
import { resolveReviewValues, ReviewValues, RowEdits } from './reviewValues'
import {
  guessInvoiceTemplate,
  matchClient,
  ParsedInvoiceSheet,
} from './uploadReview'

export interface ReviewFile {
  key: string
  file: File
}

export interface ReviewRow extends ReviewFile {
  parsed: ParsedInvoiceSheet | undefined
  template: InvoiceTemplate
  /** Matched from the sheet's company name; null when nothing matched. */
  matchedClient: ClientOptionDto | null
  /** The matched company, else the one chosen in the review. */
  client: ClientOptionDto | null
  values: ReviewValues
  /** Another file in this upload has the same company and template. */
  isDuplicate: boolean
}

/** What one file is uploaded with, as reviewed. Null leaves a value to the server. */
export type UploadChoice = Omit<UploadInvoiceFileOptions, 'billingPeriodStart'>

interface ReviewState {
  files: ReviewFile[]
  removedKeys: ReadonlySet<string>
  parsedByKey: Record<string, ParsedInvoiceSheet>
  templateByKey: Record<string, InvoiceTemplate>
  defaultTemplate: InvoiceTemplate
  editsByKey: Record<string, RowEdits>
  chosenClientByKey: Record<string, string>
  clients: ClientOptionDto[]
  oe1DueDate: string | null
}

const threadKey = (clientId: string, template: InvoiceTemplate) =>
  `${clientId}:${template}`

/** One row per file still in the upload, with its template, company and values. */
export const buildReviewRows = (state: ReviewState): ReviewRow[] => {
  const baseRows = state.files
    .filter((f) => !state.removedKeys.has(f.key))
    .map((f) => {
      const parsed = state.parsedByKey[f.key]
      const template =
        state.templateByKey[f.key] ??
        guessInvoiceTemplate(parsed, state.defaultTemplate)
      const matchedClient = matchClient(
        parsed?.companyName ?? null,
        state.clients
      )
      const chosenId = state.chosenClientByKey[f.key]
      const client =
        matchedClient ?? state.clients.find((c) => c.id === chosenId) ?? null
      return {
        ...f,
        parsed,
        template,
        matchedClient,
        client,
        values: resolveReviewValues(template, parsed, state.editsByKey[f.key], {
          oe1DueDate: state.oe1DueDate,
          profileDueDate: client?.profileDueDate ?? null,
        }),
      }
    })

  // One invoice per company and template, so a second file would replace the first.
  const countByThread = new Map<string, number>()
  for (const row of baseRows) {
    if (isNil(row.client)) continue
    const key = threadKey(row.client.id, row.template)
    countByThread.set(key, (countByThread.get(key) ?? 0) + 1)
  }
  return baseRows.map((row) => ({
    ...row,
    isDuplicate:
      !isNil(row.client) &&
      (countByThread.get(threadKey(row.client.id, row.template)) ?? 0) > 1,
  }))
}

export const toUploadChoice = ({
  template,
  values,
  matchedClient,
  client,
}: ReviewRow): UploadChoice => ({
  invoiceTemplate: template,
  clientId: isNil(matchedClient) ? (client?.id ?? null) : null,
  dueDate: values.dueDate,
  totalDue: values.amount,
  balanceDue: values.balanceDue,
  oeTotalDue: values.oeTotal,
})

/** Preview request for a file not uploaded yet; empty values are left for the server to fill. */
export const toDraftEmailRequest = (
  row: ReviewRow,
  billingPeriodStart: string
): PreviewDraftEmailRequestDto => {
  const { values } = row
  const request: PreviewDraftEmailRequestDto = {
    invoiceTemplate: row.template,
    billingPeriodStart,
  }
  const clientId = row.client?.id
  const companyName = row.client?.name ?? row.parsed?.companyName
  if (!isNil(clientId)) request.clientId = clientId
  if (!isNil(companyName)) request.companyName = companyName
  if (!isNil(values.amount)) request.amount = values.amount
  if (!isNil(values.dueDate)) request.dueDate = values.dueDate
  if (row.template !== InvoiceTemplate.OE_FIRST) return request
  if (!isNil(values.balanceDue)) request.balanceDue = values.balanceDue
  return request
}
